import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json({ locale: detectLocale(request.url) });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = data?.locale || "vi";
  return [
    { title: locale === "vi" ? "Chính sách bảo mật — Diolo" : "Privacy Policy — Diolo" },
    { name: "description", content: locale === "vi" ? "Chính sách bảo mật Diolo — cách chúng tôi thu thập, sử dụng và bảo vệ dữ liệu của bạn." : "Diolo privacy policy — how we collect, use, and protect your data." },
  ];
};

export default function PrivacyPage() {
  const { locale } = useOutletContext<{ locale: Locale }>();

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs locale={locale} items={[{ label: t("privacyPolicy", locale) }]} />
      {/* Header */}
      <div className="text-center mb-16">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {t("legal", locale)}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {locale === "vi" ? "Chính sách bảo mật" : "Privacy Policy"}
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-luxury-stone text-sm sm:text-base leading-relaxed">
          {locale === "vi"
            ? "Cập nhật lần cuối: Tháng 5, 2026"
            : "Last updated: May 2026"}
        </p>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto space-y-12">
        {/* Data Collection */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Thu thập dữ liệu" : "Data Collection"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Chúng tôi thu thập thông tin cá nhân mà bạn cung cấp trực tiếp khi đặt hàng, tạo tài khoản hoặc liên hệ với chúng tôi. Thông tin này bao gồm tên, địa chỉ email, địa chỉ giao hàng và thông tin thanh toán."
              : "We collect personal information that you provide directly when placing an order, creating an account, or contacting us. This includes your name, email address, shipping address, and payment information."}
          </p>
        </section>

        {/* How We Use Your Data */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Cách chúng tôi sử dụng dữ liệu" : "How We Use Your Data"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Dữ liệu của bạn được sử dụng để xử lý đơn hàng, cải thiện dịch vụ, gửi thông tin cập nhật về đơn hàng và (nếu bạn đồng ý) gửi thông tin về sản phẩm mới và khuyến mãi."
              : "Your data is used to process orders, improve our services, send order updates, and (with your consent) send information about new products and promotions."}
          </p>
        </section>

        {/* Cookies */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            Cookies
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Trang web của chúng tôi sử dụng cookies để duy trì phiên đăng nhập, lưu giỏ hàng và phân tích lưu lượng truy cập. Bạn có thể tắt cookies trong trình duyệt, nhưng một số tính năng có thể không hoạt động đầy đủ."
              : "Our website uses cookies to maintain your session, save your cart, and analyse site traffic. You can disable cookies in your browser settings, but some features may not function fully."}
          </p>
        </section>

        {/* Data Protection */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Bảo vệ dữ liệu" : "Data Protection"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Chúng tôi áp dụng các biện pháp bảo mật tiêu chuẩn ngành để bảo vệ thông tin cá nhân của bạn. Thông tin thanh toán được mã hóa và xử lý qua các nhà cung cấp thanh toán uy tín."
              : "We implement industry-standard security measures to protect your personal information. Payment data is encrypted and processed through trusted payment providers."}
          </p>
        </section>

        {/* Third-Party Sharing */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Chia sẻ với bên thứ ba" : "Third-Party Sharing"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Chúng tôi không bán thông tin cá nhân của bạn. Dữ liệu chỉ được chia sẻ với các đối tác vận chuyển và thanh toán cần thiết để hoàn thành đơn hàng của bạn."
              : "We do not sell your personal information. Data is only shared with shipping and payment partners necessary to fulfil your orders."}
          </p>
        </section>

        {/* Messenger Channel Matching */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi"
              ? "Đối chiếu tài khoản Messenger"
              : "Messenger Account Matching"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Khi bạn nhắn tin cho chúng tôi qua Facebook Messenger sử dụng số điện thoại đã đăng ký mua hàng tại Diolo, hoặc khi bạn đang đăng nhập trên website và bấm nút \"Nhắn tin\" trên trang sản phẩm, chúng tôi có thể liên kết tài khoản Messenger (Page-Scoped User ID do Meta cung cấp) với hồ sơ khách hàng của bạn nhằm: (1) cung cấp dịch vụ chăm sóc khách hàng cá nhân hóa hơn, (2) hiển thị lịch sử đơn hàng cho nhân viên hỗ trợ để tra cứu nhanh, và (3) gửi cập nhật trạng thái đơn hàng qua Messenger khi cần thiết."
              : "When you contact us via Facebook Messenger using a phone number you previously used to place an order at Diolo, or when you are logged in on our website and click the \"Message us\" button on a product page, we may link your Messenger account (the Page-Scoped User ID provided by Meta) to your customer profile in order to: (1) provide more personalised customer service, (2) display order history to our support team for faster lookup, and (3) send order status updates via Messenger when relevant."}
          </p>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed mt-3">
            {locale === "vi"
              ? "Chúng tôi lưu Page-Scoped User ID, tên (do Meta cung cấp), ảnh đại diện (nếu có) và ngôn ngữ ưu tiên. Bạn có quyền yêu cầu xóa liên kết này bất cứ lúc nào bằng cách: (a) liên hệ chúng tôi qua hello@diolo.com, hoặc (b) gỡ ứng dụng \"Diolo\" khỏi tài khoản Facebook của bạn — Meta sẽ tự động thông báo cho chúng tôi và liên kết sẽ bị xóa trong vòng 24 giờ."
              : "We store the Page-Scoped User ID, your Meta-provided display name, profile photo (if available), and language preference. You may request removal of this link at any time by: (a) contacting us at hello@diolo.com, or (b) removing the \"Diolo\" app from your Facebook account — Meta will notify us automatically and the link will be deleted within 24 hours."}
          </p>
        </section>

        {/* Your Rights */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Quyền của bạn" : "Your Rights"}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Bạn có quyền truy cập, chỉnh sửa hoặc xóa dữ liệu cá nhân của mình. Để thực hiện các quyền này, vui lòng liên hệ chúng tôi qua email."
              : "You have the right to access, correct, or delete your personal data. To exercise these rights, please contact us via email."}
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-4">
            {t("contactShort", locale)}
          </h2>
          <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
            {locale === "vi"
              ? "Nếu bạn có câu hỏi về chính sách bảo mật này, vui lòng liên hệ chúng tôi tại hello@diolo.com."
              : "If you have questions about this privacy policy, please contact us at hello@diolo.com."}
          </p>
        </section>
      </div>
    </div>
  );
}
